import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';

const variants = {
  solid: 'bg-brandSecondary text-white text-semibold',
  outline:
    'border border-brandSecondary bg-transparent  text-brandSecondary hover:bg-brandSecondary hover:text-white',
  ghost: 'hover:bg-accent hover:text-accent-foreground',
  link: 'text-primary underline-offset-4 hover:underline',
  destructive:
    'bg-destructive text-destructive-foreground hover:bg-destructive/90',
  secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
};

const sizes = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-9 px-4 text-sm',
  lg: 'h-10 px-0 py-0 text-base', //px-6 removed
  icon: 'h-9 w-9',
};

export const Button = React.forwardRef(
  (
    {
      children,
      className,
      variant = 'solid',
      size = 'md',
      isLoading = false,
      loadingText = 'Loading...',
      leftIcon,
      rightIcon,
      // Fixed left icon column plus a vertical rule, so icons line up across
      // buttons whose labels are different lengths.
      splitIcon = false,
      type = 'button',
      disabled,
      form,
      name,
      value,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center rounded-md font-medium  focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 text-bold';

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || isLoading}
        className={clsx(
          baseStyles,
          variants[variant],
          sizes[size],
          splitIcon && '!justify-start !px-0',
          className
        )}
        form={form}
        name={name}
        value={value}
        aria-disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <svg
              className="mr-2 h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>{loadingText}</span>
          </>
        ) : (
          <>
            {splitIcon && leftIcon ? (
              <>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center">
                  {leftIcon}
                </span>
                <span
                  className="h-5 w-px shrink-0 self-center bg-current"
                  aria-hidden="true"
                />
                <span className="px-3">{children}</span>
              </>
            ) : (
              <>
                {leftIcon && <span className="mr-2">{leftIcon}</span>}
                {children}
                {rightIcon && <span className="ml-2">{rightIcon}</span>}
              </>
            )}
          </>
        )}
      </button>
    );
  }
);

Button.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  variant: PropTypes.oneOf(Object.keys(variants)),
  size: PropTypes.oneOf(Object.keys(sizes)),
  isLoading: PropTypes.bool,
  loadingText: PropTypes.string,
  leftIcon: PropTypes.node,
  rightIcon: PropTypes.node,
  splitIcon: PropTypes.bool,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  disabled: PropTypes.bool,
  form: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  onClick: PropTypes.func,
};

Button.displayName = 'Button';

export default Button;
